// Chain Rule content is adapted from ARC's public lesson preview.
// The Product Rule is a complementary sample for this self-contained demo.
export const lessons = [
  {
    id: 'chain', title: 'The Chain Rule', subtitle: 'Differentiating composite functions',
    chapter: '01', formula: 'h′(x) = f′(g(x)) · g′(x)',
    intro: 'Some functions live inside other functions. The chain rule lets us find how the whole expression changes, one layer at a time.',
    example: 'd/dx [sin(x²)] = cos(x²) · 2x',
    steps: [
      ['Find the layers', 'The outer function is sin(u). The inner function is u = x².'],
      ['Differentiate the outside', 'The derivative of sin(u) is cos(u). Keep x² inside for now.'],
      ['Multiply by the inside', 'The derivative of x² is 2x. Multiply the two rates to get 2x cos(x²).'],
    ],
    questions: [
      ['Why multiply?', 'Composing functions chains their rates of change. The outer function changes with the inner value, while the inner value changes with x. Multiplying those rates gives the overall rate with respect to x.'],
      ['Show another example', 'Take y = (3x + 1)⁴. Differentiate the outer power: 4(3x + 1)³. Then multiply by the inner derivative, 3. The result is 12(3x + 1)³.'],
      ['Give me a hint', 'Look for a function nested inside another: a power inside a sine, or a linear expression inside a power. Name the inner expression u, then differentiate each layer.'],
    ],
    prompt: 'Find the derivative of cos(3x²).',
    answers: ['−sin(3x²)', '−6x sin(3x²)', '6x cos(3x²)'], correct: 1,
    wrong: 'Remember the inner derivative: 3x² changes at a rate of 6x. Multiply it by the derivative of cosine.',
    feedback: 'Exactly right. The outer derivative is −sin(3x²); multiplying by the inner derivative, 6x, gives −6x sin(3x²).',
  },
  {
    id: 'product', title: 'The Product Rule', subtitle: 'When two functions multiply',
    chapter: '02', formula: '(fg)′ = f′g + fg′',
    intro: 'When both factors change, each contributes to the derivative. Differentiate one factor at a time and add the two contributions.',
    example: 'd/dx [x² sin(x)] = 2x sin(x) + x² cos(x)',
    steps: [
      ['Separate the factors', 'Let f(x) = x² and g(x) = sin(x). Both depend on x.'],
      ['Find both derivatives', 'The derivative of x² is 2x. The derivative of sin(x) is cos(x).'],
      ['Add the contributions', 'First derivative times second, plus first times second derivative: 2x sin(x) + x² cos(x).'],
    ],
    questions: [
      ['Why add?', 'Both factors change. One contribution comes from the change in f while g is held fixed; the other comes from the change in g while f is held fixed. Their rates of change add together.'],
      ['Show another example', 'For y = x cos(x), the first derivative is 1 and the second is −sin(x). Apply the rule: y′ = cos(x) − x sin(x).'],
      ['Give me a hint', 'Keep one factor unchanged while differentiating the other. Do this for each factor, then add the two terms.'],
    ],
    prompt: 'Find the derivative of x² sin(x).',
    answers: ['2x cos(x)', '2x sin(x) + x² cos(x)', 'x² cos(x)'], correct: 1,
    wrong: 'Both factors depend on x. Include the term from differentiating x² as well as the term from differentiating sin(x).',
    feedback: 'Exactly right. Differentiating each factor in turn gives 2x sin(x) + x² cos(x).',
  },
]
